import { useEffect, useRef, useState } from 'react'

import { communities } from '@api/swagger'
import { useAppSnackbar } from '@hooks/useAppSnackbar'
import useLoadingContext from '../../hooks/useLoadingContext'
import { usePagination } from '@hooks/usePagination'
import {
  CommunityInviteItem,
  FetchCommunityInvitesStatusEnum,
  PaginationResponse,
  OrderByDirection,
} from '@swagger'
import { MINIMUM_DEBOUNCED_SEARCH_QUERY_LENGTH } from '@utils/constants'

import { CommunityInviteRow } from './getCommunityInvitesTableColumns'

export const ALL_STATUSES = 'AllStatuses'

const toCommunityInviteRow = (
  invite: CommunityInviteItem
): CommunityInviteRow => {
  const { offeredSpots, skippedSpots } = invite.programs.reduce(
    (acc, program) => {
      acc.offeredSpots += program.offeredSpots
      acc.skippedSpots += program.skippedSpots ?? 0
      return acc
    },
    { offeredSpots: 0, skippedSpots: 0 }
  )

  return {
    id: invite.uuid,
    familyName: invite.parentLastName,
    sentToEmail: invite.sentToEmail,
    invitationDate: invite.invitationDate,
    resentDate: invite.resentDate,
    programCount: invite.programs.length,
    offeredSpots,
    skippedSpots,
    status: invite.status,
    programs: invite.programs,
    parentUserKey: invite.parentUserKey,
  }
}

export interface UseCommunityInvitesProps {
  selectedCommunityKey?: number
  academicYear?: number
  fetchCommunityInvitesLoadingId: string
  initialStatusFilter?: FetchCommunityInvitesStatusEnum
}

export interface UseCommunityInvitesReturn {
  invites: CommunityInviteRow[]
  search: string
  statusFilter: FetchCommunityInvitesStatusEnum | undefined
  hasFetchedOnce: boolean
  hasFetchError: boolean
  isFetchingInvites: boolean
  pagination: PaginationResponse
  handleSearch: (searchText: string) => void
  handleStatusSelection: (
    _: string,
    __: string,
    selectedId: string | number
  ) => void
  handlePaginationChange: (newPagination: PaginationResponse) => void
  triggerFetch: () => void
}

export const useCommunityInvites = ({
  selectedCommunityKey,
  academicYear,
  fetchCommunityInvitesLoadingId,
  initialStatusFilter,
}: UseCommunityInvitesProps): UseCommunityInvitesReturn => {
  const { handleError } = useAppSnackbar()

  const [invites, setInvites] = useState<CommunityInviteRow[]>([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<
    FetchCommunityInvitesStatusEnum | undefined
  >(initialStatusFilter)
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false)
  const [hasFetchError, setHasFetchError] = useState(false)
  const latestRequestIdRef = useRef(0)

  const { pagination, setPage, setPageSize, setOrderBy, setTotalCount } =
    usePagination({
      initialOrderBy: [{ sentToEmail: OrderByDirection.Asc }],
    })

  const fetchInvites = async () => {
    if (!selectedCommunityKey || !academicYear) return

    const requestId = ++latestRequestIdRef.current

    setHasFetchError(false)

    try {
      const response = await communities.fetchCommunityInvites({
        communityKey: selectedCommunityKey,
        academicYear,
        search: search || undefined,
        status: statusFilter,
        page: pagination.page,
        pageSize: pagination.pageSize,
        orderBy: pagination.orderBy?.length
          ? pagination.orderBy.map((clause) => {
              let field = Object.keys(clause)[0]
              const direction = Object.values(clause)[0]
              if (field === 'familyName') field = 'parentLastName'
              return `${field} ${direction}`
            })
          : undefined,
      })

      if (latestRequestIdRef.current !== requestId) return

      setInvites(response.communityInvites.map(toCommunityInviteRow))
      setTotalCount(response.pagination?.totalCount ?? 0)
      setHasFetchError(false)
    } catch (error) {
      if (latestRequestIdRef.current !== requestId) return

      setInvites([])
      setTotalCount(0)
      setHasFetchError(true)

      await handleError(error)
    }
  }

  const { triggerFetch, isLoading: isFetchingInvites } = useLoadingContext({
    asyncFunction: fetchInvites,
    loadingId: fetchCommunityInvitesLoadingId,
    allowReentry: true,
  })

  const handleSearch = (searchText: string) => {
    const trimmedSearch = searchText.trim()

    if (
      trimmedSearch.length === 0 ||
      trimmedSearch.length >= MINIMUM_DEBOUNCED_SEARCH_QUERY_LENGTH
    ) {
      setPage(1)
      setSearch(trimmedSearch)
    }
  }

  const handleStatusSelection = (
    _: string,
    __: string,
    selectedId: string | number
  ) => {
    setPage(1)

    setStatusFilter(
      selectedId === ALL_STATUSES
        ? undefined
        : (selectedId as FetchCommunityInvitesStatusEnum)
    )
  }

  const handlePaginationChange = (newPagination: PaginationResponse) => {
    setPageSize(newPagination.pageSize)
    setOrderBy(newPagination.orderBy)
    setPage(newPagination.page + 1)
  }

  useEffect(() => {
    if (isFetchingInvites) {
      setHasFetchedOnce(true)
    }
  }, [isFetchingInvites])

  useEffect(() => {
    triggerFetch()
  }, [
    selectedCommunityKey,
    academicYear,
    search,
    statusFilter,
    pagination.page,
    pagination.pageSize,
    pagination.orderBy,
    triggerFetch,
  ])

  return {
    invites,
    search,
    statusFilter,
    hasFetchedOnce,
    hasFetchError,
    isFetchingInvites,
    pagination,
    handleSearch,
    handleStatusSelection,
    handlePaginationChange,
    triggerFetch,
  }
}
